// ============================================================================
// The ask box (item #737, part of #700; TCSD-0416, TCSD-0417).
//
// Drawn to the approved 7 Oct demo: under the morning message, the conversation,
// then the box and three suggestions. It is Ask AI — the same conversation, saved
// in Ask AI's history — with two tools only superusers get: it reads the board by
// the page's own rules, and it drafts approve / push back / park / unpark /
// priority / pin as a card Ben confirms with one press. "Do it" is the same write
// the buttons make; "Discard" writes nothing. Merge stays on its button.
// ============================================================================
import { useEffect, useRef, useState } from 'react';
import { useCopilot } from '../copilot/hooks/use-copilot';
import { ChatMessage } from '../copilot/chat-message';
import type { CopilotProposalBase } from '../../shared/types';
import type { BoardItem } from './derive';

const short = (t: string) => (t.length > 48 ? `${t.slice(0, 46).trimEnd()}…` : t);

/**
 * The demo's three suggestions. The third names the first item in Needs you that
 * has a write-up to approve, and is left out when there is none.
 */
export function askSuggestions(needs: readonly BoardItem[]): string[] {
  const out = ['What should I decide first, and why?', 'Where do open items collide most?'];
  const t = needs.find((i) => i.write_up && i.labels.includes('stage:needs-decision'));
  if (t) out.push(`Approve the recommendation on "${short(t.title)}"`);
  return out;
}

export function AskBox({ needs }: { needs: readonly BoardItem[] }) {
  const { messages, isSending, error, sendMessage, commitProposal, discardProposal } = useCopilot();
  const [text, setText] = useState('');
  const [committingId, setCommittingId] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const end = useRef<HTMLDivElement>(null);

  useEffect(() => {
    end.current?.scrollIntoView({ block: 'nearest' });
  }, [messages.length, isSending]);

  const send = (q: string) => {
    const v = q.trim();
    if (!v || isSending) return;
    setText('');
    setRefused(null);
    void sendMessage(v);
  };

  const onCommit = async (p: CopilotProposalBase, input: Record<string, unknown>, wasEdited: boolean) => {
    setCommittingId(p.action_id);
    setRefused(null);
    const r = await commitProposal(p.action_id, input, wasEdited, () => null);
    setCommittingId(null);
    // The bridge's own sentence, never a blank.
    if (!r.ok) setRefused(r.error ?? 'It was not written.');
  };

  return (
    <div aria-label="The ask box" className="mt-2">
      {messages.length > 0 && (
        <div className="max-h-[300px] space-y-2.5 overflow-y-auto pr-1">
          {messages.map((m, k) => (
            <ChatMessage
              key={k}
              message={m}
              facilities={[]}
              committingId={committingId}
              onCommit={(p, input, wasEdited) => void onCommit(p, input, wasEdited)}
              onDiscard={(p) => void discardProposal(p.action_id)}
            />
          ))}
          {isSending && <p className="text-[12px] text-tcs-text-muted">Reading the board…</p>}
          <div ref={end} />
        </div>
      )}
      {(error || refused) && (
        <div role="alert" className="mt-2 rounded-lg border border-st-danger-border bg-st-danger-bg px-3 py-2 text-[13px] text-tcs-text">
          {refused ?? error}
        </div>
      )}
      <form
        className="mt-2 flex gap-1.5"
        onSubmit={(e) => {
          e.preventDefault();
          send(text);
        }}
      >
        <input
          aria-label="Ask the command center"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Ask about the board, or tell it what to do"
          className="min-w-0 flex-1 rounded-md border border-tcs-border bg-tcs-bg-muted px-2.5 py-2 text-[13px] text-tcs-text"
        />
        <button
          type="submit"
          disabled={isSending || !text.trim()}
          className="rounded-md border border-tcs-accent bg-tcs-accent px-3 py-1 text-[12px] font-semibold text-tcs-bg disabled:cursor-not-allowed disabled:opacity-45"
        >
          Ask
        </button>
      </form>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {askSuggestions(needs).map((q) => (
          <button
            key={q}
            type="button"
            disabled={isSending}
            onClick={() => send(q)}
            className="rounded-full border border-tcs-border px-2.5 py-1 text-[12px] text-tcs-text-secondary hover:text-tcs-text disabled:opacity-45"
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}
